import { useCallback, useEffect, useState, type FC } from "react";
import { flowRun, type FlowStatus, type FlowStep, type FlowRun } from "../../api";
import { AppBar, Progress } from "./PhoneFrame";
import { AdviceScreen } from "./AdviceScreen";
import {
  FinancialSituationStep,
  RiskStep,
  ProductAssessmentStep,
  SustainabilityStep,
  GoalPurposeStep,
  PortfolioAmountStep,
  FlowFieldsStep,
  type StepProps,
} from "./steps";

// Component types we render a bespoke, interactive screen for. Display-only
// components resolve on their own; a step we can't render is simply skipped.
const SUPPORTED: Record<string, FC<StepProps>> = {
  financial_situation: FinancialSituationStep,
  risk_assessment: RiskStep,
  goal_input: GoalPurposeStep,
  portfolio_input: PortfolioAmountStep,
  product_assessment: ProductAssessmentStep,
  sustainability: SustainabilityStep,
};

// The renderer for an open step: bespoke by componentType, else the flow-fields
// screen for any `field`-kind step (advice metadata).
function rendererFor(step: FlowStep): FC<StepProps> | null {
  const byType = SUPPORTED[step.componentType ?? ""];
  if (byType) return byType;
  if (step.kind === "field") return FlowFieldsStep;
  return null;
}

// A stable key so loop-protection can tell "the same step is still open after I
// submitted it" (i.e. it won't complete over the API) from genuine progress.
function stepKey(step: FlowStep): string {
  return (
    step.componentId ??
    `${step.componentType ?? step.kind}:${step.goalId ?? ""}:${step.fieldId ?? ""}`
  );
}

export function FlowRunner({
  run,
  onExit,
}: {
  run: FlowRun;
  onExit: () => void;
}) {
  const runId = run.id;
  const [status, setStatus] = useState<FlowStatus | null>(null);
  const [mode, setMode] = useState<"loading" | "step" | "advice" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  // Once the investor reaches the advice reveal we keep them there even if some
  // display steps are still technically "open".
  const [reachedAdvice, setReachedAdvice] = useState(false);
  // Steps that stayed open after we submitted them (won't complete over the
  // API) — attempted once, then skipped so the runner never loops. See
  // docs/quantfolio-api-notes.md #6 (goal_input).
  const [stuck, setStuck] = useState<Set<string>>(new Set());
  // Ordered history of the steps we've shown, so the app-bar back arrow can
  // return to an earlier step. reviewIdx === null means we're at the live
  // frontier; a number means we're reviewing trail[reviewIdx].
  const [trail, setTrail] = useState<FlowStep[]>([]);
  const [reviewIdx, setReviewIdx] = useState<number | null>(null);

  const candidates = (st: FlowStatus, skip: Set<string>): FlowStep[] =>
    st.steps.filter(
      (s) => s.status === "open" && rendererFor(s) && !skip.has(stepKey(s)),
    );

  // `justSubmitted` is the key of the step we just tried; if it's still open,
  // mark it stuck.
  const load = useCallback(
    (justSubmitted?: string) => {
      setMode("loading");
      flowRun
        .status(runId)
        .then((d) => {
          setStatus(d.status);
          let skip = stuck;
          if (
            justSubmitted &&
            d.status.steps.some(
              (s) => s.status === "open" && stepKey(s) === justSubmitted,
            )
          ) {
            skip = new Set(stuck).add(justSubmitted);
            setStuck(skip);
          }
          const next = candidates(d.status, skip)[0];
          if (reachedAdvice || !next) {
            setReachedAdvice(true);
            setMode("advice");
          } else {
            const step = next;
            setTrail((t) =>
              t.length && stepKey(t[t.length - 1]!) === stepKey(step) ? t : [...t, step],
            );
            setReviewIdx(null);
            setMode("step");
          }
        })
        .catch((e) => {
          setErr(e.message);
          setMode("error");
        });
    },
    [runId, reachedAdvice, stuck],
  );

  useEffect(() => {
    load();
  }, [load]);

  const openStep = status ? (candidates(status, stuck)[0] ?? null) : null;

  // Once at least one goal has a risk score, advice can be shown — offer a
  // permanent escape hatch so a fiddly suitability step never traps the user.
  const canAdvise =
    (status?.steps.some(
      (s) => s.componentType === "risk_assessment" && s.status === "complete",
    ) ?? false) ||
    (status?.percent ?? 0) >= 40;

  const jumpToAdvice = () => {
    setReachedAdvice(true);
    setMode("advice");
  };

  // Step-back: the app-bar arrow returns to the previous step (exiting the app
  // is handled by "Close app" in the outer top bar).
  const shownStep = reviewIdx !== null ? (trail[reviewIdx] ?? null) : openStep;
  const hasPrev = reviewIdx !== null ? reviewIdx > 0 : trail.length >= 2;
  const goBack = () => {
    if (reviewIdx !== null) {
      if (reviewIdx > 0) setReviewIdx(reviewIdx - 1);
    } else if (trail.length >= 2) {
      setReviewIdx(trail.length - 2);
    }
  };

  return (
    <>
      <AppBar
        title={run.flow_name ?? "Your plan"}
        onBack={mode === "step" && hasPrev ? goBack : undefined}
      />
      {status && mode !== "advice" && <Progress percent={status.percent} />}
      {mode === "step" && canAdvise && (
        <button type="button" className="iapp-skip" onClick={jumpToAdvice}>
          See my advice →
        </button>
      )}

      {mode === "loading" && (
        <div className="iapp-center">
          <div className="iapp-spinner" />
          <span>One moment…</span>
        </div>
      )}

      {mode === "error" && (
        <div className="iapp-view">
          <div className="iapp-error">{err}</div>
        </div>
      )}

      {mode === "step" &&
        shownStep &&
        (() => {
          const Comp = rendererFor(shownStep);
          const key = stepKey(shownStep);
          return Comp ? (
            <Comp
              runId={runId}
              step={shownStep}
              onDone={() => {
                setReviewIdx(null);
                load(key);
              }}
            />
          ) : null;
        })()}

      {mode === "advice" && <AdviceScreen runId={runId} onExit={onExit} />}
    </>
  );
}
